import { PropsWithChildren } from 'react'
import { Helmet } from 'react-helmet'
import { ErrorBoundary } from 'react-error-boundary'
import { Main } from '../../components'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useBlizzardApi, useLogo, useRefreshData } from '../../helpers'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import GuildHeader from './components/GuildHeader'
import { useGuildDetails, useTeamDetails } from '../../config/queries'
import { Button } from '@/components/ui/button'
import { updateGuild } from '../../api/guild'
import GuildContext from '../../config/context/guild'
import TeamContext from '../../config/context/team'
import PageError from '../../components/PageError'
import ProtectedContent from '../../components/ProtectedContent'
import { AccessLevel, Section } from 'app/frontend/types/custom'
import { Progress } from '@/components/ui/progress'
import useReferenceStore from '../../state/useReferenceStore'
import useBasePath from '../../hooks/useBasePath'
import NotFound from '../Error/NotFound'

interface Props {
  scope: 'guild' | 'team'
  teamSection?: Section
  teamRequirement?: AccessLevel
}

const GuildDashboard = ({
  children,
  scope,
  teamSection = 'base',
  teamRequirement = 'member',
}: PropsWithChildren<Props>) => {
  const baseGuildPath = useBasePath('guild')
  const baseTeamPath = useBasePath('team')

  const { t } = useTranslation()
  const { getReference } = useReferenceStore()
  const { Logo } = useLogo()

  const {
    uriRegion,
    uriRealm,
    uriName,
    blizzardInfo,
    blizzardStatus,
    searchBlizzard,
  } = useBlizzardApi<wowaudit.Guild>('guild', t)

  const guildReference =
    blizzardInfo ?? getReference<wowaudit.GuildReference>(baseGuildPath)
  const teamReference = getReference<wowaudit.TeamReference>(baseTeamPath)

  const {
    refetch,
    data: guild,
    isPending: isGuildPending,
  } = useGuildDetails(guildReference?.id)

  const {
    data: team,
    refetch: refetchTeamDetails,
    isPending: isTeamPending,
  } = useTeamDetails(teamReference?.id)

  const { refreshing, refreshData, updateTriggeredAt } = useRefreshData({
    retriever: updateGuild,
    id: guildReference?.id,
    successKey: 'dashboard.guild.guild-update-success',
    callback: refetch,
    queryToInvalidate: ['guild', { path: baseGuildPath }],
  })

  return (
    <Main title={guild?.name}>
      <GuildContext.Provider
        value={{
          guild,
          guildReference,
          currentTeam: team,
          teamReference:
            (team as wowaudit.TeamReference | undefined) ?? teamReference,
        }}
      >
        <GuildHeader
          uriName={uriName}
          uriRegion={uriRegion}
          uriRealm={uriRealm}
        />

        {guild?.id && !guild.exists ? (
          <div className='mt-8'>
            <Alert variant='warning'>
              <AlertTitle>{t('dashboard.guild.no-longer-exists')}</AlertTitle>
              <AlertDescription className='mt-4'>
                <p>
                  <Trans
                    i18nKey='dashboard.guild.no-longer-exists-description'
                    components={[
                      <Link
                        key='settings'
                        to={`${guild.path}/settings`}
                        className='text-primary'
                      />,
                    ]}
                  />
                </p>

                <Button
                  variant='outline'
                  size='sm'
                  loading={refreshing}
                  spinner='blizzard'
                  className='flex items-center mt-4 gap-3 min-w-30 text-foreground'
                  disabled={!guild?.id || !!updateTriggeredAt}
                  onClick={() => refreshData()}
                >
                  <Logo className='w-6 h-6' />
                  {updateTriggeredAt
                    ? t('dashboard.guild.not-found')
                    : t('shared.refresh')}
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        ) : null}
      </GuildContext.Provider>

      {!guildReference?.id ? (
        <div className='mt-8'>
          <Alert variant='warning'>
            <AlertTitle>{t('dashboard.guild.not-found')}</AlertTitle>
            <AlertDescription className='mt-4'>
              <p>{t('dashboard.guild.not-found-description')}</p>
              <Button
                className='mt-2 text-foreground'
                variant='outline'
                loading={blizzardStatus === 'pending'}
                disabled={blizzardStatus === 'not-found'}
                spinner='blizzard'
                onClick={searchBlizzard}
              >
                {t(
                  blizzardStatus === 'not-found'
                    ? 'dashboard.guild.not-found'
                    : `header.search-official-api`,
                )}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <ErrorBoundary
          FallbackComponent={PageError}
          resetKeys={[location.pathname]}
        >
          {isGuildPending || (teamReference?.id && isTeamPending) ? (
            <Progress
              className='-mx-8 w-[calc(100%+4rem)] rounded-none bg-transparent h-1'
              indeterminate
            />
          ) : (
            <div className='my-6'>
              <GuildContext.Provider
                value={{
                  guild,
                  guildReference,
                  currentTeam: team,
                  teamReference,
                }}
              >
                {scope === 'guild' ? (
                  children
                ) : !team ? (
                  <NotFound message={'Are you sure that this team exists?'} />
                ) : (
                  <ProtectedContent
                    section={teamSection}
                    requirement={teamRequirement}
                  >
                    {team && guild ? (
                      <TeamContext.Provider
                        value={{ guild, team, refetchTeamDetails }}
                      >
                        <Helmet>
                          <title>
                            {team?.name}, {guild?.name} | wowaudit
                          </title>
                        </Helmet>

                        {children}
                      </TeamContext.Provider>
                    ) : null}
                  </ProtectedContent>
                )}
              </GuildContext.Provider>
            </div>
          )}
        </ErrorBoundary>
      )}
    </Main>
  )
}

export default GuildDashboard
